<script>
/**
 * ReportTable is a the component that renders the <table>-element in reports.
 * It takes two props, columns and rows, which come from the backend API.
 *
 */

import moment from 'moment';
import { columnNames, columnValues } from '@/lib/reportTranslations';

/**
 * A typical column definition looks like this:
 * { format: "date", name: "paidDate", nullable: true, type: "string" }
 * - 'type' indicates data type, such as string, integer or float.
 * - 'format' indicates a specific formatting to be applied when rendering,
 *   such as date or currency.
 *
 * 'formatters' is a map of formats for each data type.
 *
 * When rendering a cell it will look for a formatter like this: formatters[type][format].
 * If no format is specified it will use 'default'.
 * If no type is specified it will use 'string'.
 */
const formatters = {
  string: {
    date: v => {
      if (!v) {
        return '';
      }
      const localDate = moment.utc(v, 'YYYY-MM-DD').local();
      if (!localDate.isValid()) {
        return v;
      }
      return localDate.format('DD.MM.YYYY');
    },
    default: (v, col) => {
      // Replace with the localized string if this column is localized.
      if (col.name && columnValues[col.name]) {
        return columnValues[col.name][v] || v;
      }
      return v;
    },
  },

  integer: {
    default: v => {
      return v;
    },
  },

  float: {
    currency: function (v) {
      return this.$options.filters.toCurrency(v);
    },
    default: v => {
      return v;
    },
  },
};

export default {
  name: 'ReportTable',

  props: {
    columns: Array,
    rows: Array,
  },

  computed: {
    /**
     * This is done as a computed to cache the results and have the
     * cache invalidated when rows or columns change.
     */
    formattedRows() {
      return this.rows.map(row => row.map(this.formatCell));
    },
  },

  methods: {
    // This is called for every cell
    formatCell(cell, colIndex) {
      // The column definition for this cell
      const col = this.columns[colIndex];

      if (col) {
        let type = col.type;
        let value = cell;

        /**
         * A cell can come as either a string, which is the value,
         * or as an object.
         *
         * If the cell is an object, we need to check if the cell
         * overrides the column type.
         */
        if (cell !== null && typeof cell === 'object') {
          type = cell.type;

          /**
           * A cell that has only a name and not a value is to be considered a heading.
           */
          if (cell.name && !cell.value) {
            // Use cell name as value (since it is a heading) and localize it as a heading.
            value = typeof columnNames[cell.name] === 'undefined' ? cell.name : columnNames[cell.name];
          } else {
            value = cell.value;
          }
        }

        // Call the appropriate formatter.
        const typeFormatters = formatters[type] || formatters['string'];
        const formatter = typeFormatters[col.format] || typeFormatters['default'];
        return {
          formattedValue: formatter.call(this, value, col),
          definition: cell,
        };
      } else {
        // missing column name detected
        return undefined;
      }
    },
  },

  render(ce) {
    // Render the columns that aren't hidden
    const headerCells = this.columns
      .filter(col => !col.hidden)
      .map(col => {
        return ce('th', {}, typeof columnNames[col.name] === 'undefined' ? col.name : columnNames[col.name]);
      });

    // Render all rows
    const rows = this.formattedRows.map(row => {
      return ce(
        'tr',
        {},
        // Render each cell inside the row
        row.map(({ formattedValue, definition }, cellIndex) => {
          if (this.columns[cellIndex].hidden) {
            return null;
          }

          const attrs = {};
          const style = {};

          if (definition) {
            if (definition.colspan) {
              attrs.colspan = definition.colspan;
            }
            if (definition.bold === 1) {
              style['font-weight'] = 'bold';
            }
            if (definition.border === 1) {
              style['border-bottom'] = '2px solid var(--gray-dark, #000)';
            }
          }

          return ce('td', { attrs, style }, formattedValue);
        })
      );
    });

    const headerRow = ce('tr', {}, headerCells);
    const headerGroup = ce('thead', { class: 'thead-dark' }, [headerRow]);
    const bodyGroup = ce('tbody', {}, [rows]);

    const table = ce('table', { class: 'report-table table table-striped table-hover' }, [headerGroup, bodyGroup]);

    return table;
  },
};
</script>

<style lang="scss">
.report-table {
  position: relative;
  width: 100%;
  font-family: 'Helvetica';

  .thead-dark {
    th {
      background: white;
      color: $dark;
      border: none;
      box-shadow: $box-shadow-2;
    }
  }

  th {
    white-space: nowrap;
    position: sticky;
    top: 0;
  }

  td {
    white-space: nowrap;
    height: 1rem;
    box-sizing: content-box;
  }
}

.table-striped tbody tr:nth-of-type(odd) {
  background: $light;
}
</style>
